/**
 * Venda na Rota — etapa 1, Cliente: pesquisa por código, nome, CPF/CNPJ ou telefone com
 * sugestões enquanto digita (no servidor com conexão; na cópia do aparelho sem conexão),
 * "+ Novo cliente" (cadastro rápido sem sair da venda) e o cliente escolhido com o que falta
 * para a NF-e. Só clientes da empresa ativa (o servidor garante).
 */
import { formatDocument, type Page, type Party } from "@cafegestor/shared";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Alert, Button, Icon, SearchInput } from "@cafegestor/ui";
import { apiFetch, errorMessage } from "@cafegestor/client";
import { matchesCustomer, nfeMissing } from "./customer-lib";
import { useRotaHost } from "./host";
import { QuickCustomerForm } from "./QuickCustomerForm";

function useDebounced(value: string, ms: number) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setV(value), ms);
    return () => clearTimeout(id);
  }, [value, ms]);
  return v;
}

const line2 = (c: Party) =>
  [
    c.code,
    c.document ? formatDocument(c.document) : null,
    c.city ? `${c.city}${c.uf ? `/${c.uf}` : ""}` : null,
    c.mobile || c.phone,
  ]
    .filter(Boolean)
    .join(" · ");

export function CustomerStep({
  online,
  cached,
  customer,
  onSelect,
}: {
  online: boolean;
  /** cópia da lista de clientes neste aparelho (sem conexão) */
  cached: Party[];
  customer: Party | null;
  onSelect: (c: Party | null) => void;
}) {
  const { can } = useRotaHost();
  const canCreate = can("cadastros.clientes.cadastrar_rota") || can("cadastros.clientes.gerenciar");
  const [q, setQ] = useState("");
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const term = useDebounced(q.trim(), 250);

  const search = useQuery({
    queryKey: ["rota", "clientes", term],
    queryFn: () =>
      apiFetch<Page<Party>>(
        `/cadastros/pessoas?papel=cliente&status=active&limit=20&q=${encodeURIComponent(term)}`,
      ),
    enabled: online && term.length >= 2,
    staleTime: 30_000,
  });
  const local = term.length >= 1 ? cached.filter((c) => matchesCustomer(c, term)).slice(0, 20) : [];
  const results = online && term.length >= 2 ? (search.data?.items ?? []) : local;
  const searching = online && term.length >= 2 && search.isFetching && !search.data;

  async function selectById(id: string) {
    setError(null);
    try {
      onSelect(await apiFetch<Party>(`/cadastros/pessoas/${id}`));
      setCreating(false);
      setQ("");
    } catch (err) {
      setError(errorMessage(err));
    }
  }

  if (customer) {
    const missing = nfeMissing(customer);
    return (
      <div className="space-y-3">
        {created && (
          <p
            role="status"
            className="flex items-center gap-2 rounded-xl bg-success-soft px-3 py-2 text-sm font-medium text-success-strong"
          >
            <Icon name="check" size={18} strokeWidth={2.4} /> {created}
          </p>
        )}
        <section
          aria-label="Cliente escolhido"
          className="rounded-2xl border-2 border-primary/30 bg-card p-4 shadow-card"
        >
          <div className="flex items-start gap-3">
            <span
              aria-hidden
              className="flex size-11 shrink-0 items-center justify-center rounded-full bg-caramel-soft text-accent"
            >
              <Icon name="user" size={22} />
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-[17px] font-semibold leading-snug break-words">{customer.name}</p>
              {customer.tradeName && (
                <p className="text-sm text-muted-foreground break-words">{customer.tradeName}</p>
              )}
              <p className="mt-1 text-sm text-muted-foreground break-words">{line2(customer)}</p>
              {customer.street && (
                <p className="text-sm text-muted-foreground break-words">
                  {[customer.street, customer.number, customer.district].filter(Boolean).join(", ")}
                </p>
              )}
            </div>
          </div>
          {missing.length > 0 && (
            <p className="mt-3 rounded-lg bg-warning-soft px-3 py-2 text-[13px] text-warning-strong">
              Para a NF-e falta: {missing.join("; ")}. O Pedido de Venda pode ser feito assim.
            </p>
          )}
          <Button
            variant="secondary"
            className="mt-3 w-full"
            onClick={() => {
              setCreated(null);
              onSelect(null);
            }}
          >
            Trocar cliente
          </Button>
        </section>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <SearchInput
        className="w-full"
        inputClassName="h-12 text-base"
        value={q}
        onChange={setQ}
        label="Pesquisar cliente"
        placeholder="Nome, código, CPF/CNPJ ou telefone"
      />
      {canCreate && (
        <Button
          variant="secondary"
          size="lg"
          className="w-full border-2 border-primary/40 font-semibold text-primary"
          onClick={() => setCreating(true)}
          disabled={!online}
        >
          <Icon name="userPlus" size={20} />+ Novo cliente
        </Button>
      )}
      {canCreate && !online && (
        <p className="text-[13px] text-muted-foreground">
          Cadastrar cliente precisa de conexão. Sem conexão, escolha um cliente já cadastrado.
        </p>
      )}
      {error && <Alert tone="error">{error}</Alert>}
      {search.error !== null && online && <Alert tone="error">{errorMessage(search.error)}</Alert>}
      {searching && (
        <p role="status" className="text-sm text-muted-foreground">
          Pesquisando…
        </p>
      )}
      {term.length > 0 && !searching && results.length === 0 && (
        <p className="rounded-xl border border-dashed border-input px-4 py-5 text-center text-sm text-muted-foreground">
          Nenhum cliente encontrado{online ? "" : " na cópia deste aparelho"}.
          {canCreate && online ? " Use “+ Novo cliente”." : ""}
        </p>
      )}
      {results.length > 0 && (
        <ul aria-label="Clientes encontrados" className="space-y-2">
          {results.map((c) => (
            <li key={c.id}>
              <button
                type="button"
                onClick={() => {
                  setCreated(null);
                  onSelect(c);
                  setQ("");
                }}
                className="flex min-h-14 w-full items-center gap-3 rounded-xl border border-border bg-card px-3 py-2.5 text-left hover:border-primary/40 hover:bg-muted"
              >
                <span
                  aria-hidden
                  className="flex size-9 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground"
                >
                  <Icon name="user" size={18} />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block font-semibold leading-snug break-words">{c.name}</span>
                  <span className="block text-[13px] text-muted-foreground break-words">
                    {line2(c)}
                  </span>
                </span>
                <Icon name="chevronRight" size={18} className="shrink-0 text-muted-foreground" />
              </button>
            </li>
          ))}
        </ul>
      )}
      {term.length === 0 && (
        <p className="text-sm text-muted-foreground">
          Digite parte do nome, o código, o CPF/CNPJ ou o telefone do cliente.
        </p>
      )}
      {creating && (
        <QuickCustomerForm
          initialName={q}
          onCancel={() => setCreating(false)}
          onSelectExisting={(id) => void selectById(id)}
          onSaved={(p) => {
            setCreating(false);
            setQ("");
            setCreated(`Cliente ${p.code} — ${p.name} cadastrado e selecionado.`);
            onSelect(p);
          }}
        />
      )}
    </div>
  );
}
